import React from "react";
import { useFieldArray } from "react-hook-form";
import { FormTextControl, RowFormText } from "../Common/Forms/FormText";
import EntityModal, { ModalMode } from "../Common/EntityModal";
import { TransportDto } from "../../requests/useGetTransports";
import useUpsertTransport, { TransportUpsertDto } from "../../requests/useUpsertTransport";
import { RowUnitsSelect, UnitsSelectControl } from "../Units/UnitSelect";
import { UnitType } from "../Common/dtos/UnitDto";
import BoldButton from "../Common/BoldButton";
import { Row } from "react-bootstrap";
import { Label } from "reactstrap";
import { FormSiteAssignment } from "../Common/SiteAssignment/FormSiteAssignment";
import useGetTransportSiteAssignment from "../../requests/useGetTransportSiteAssignment";
import useEntityForm from "../Common/useEntityForm";

interface CreateEditTransportProps {
  initialTransport: TransportDto | null;
  onDismiss: () => void;
  onUpsert: (transportId: string, transportName: string) => void;
  mode: ModalMode;
}

export const CreateEditTransport = (props: CreateEditTransportProps) => {
  const { id, sites: initialSites, ...initialTransport } = { ...props.initialTransport };

  const { hookProps, control, getValues, submit, isSaving, loading, loadError, displayMessage, sites } =
    useEntityForm<TransportUpsertDto, TransportUpsertDto>({
      initialId: props.initialTransport?.id,
      defaultValues: {
        ...initialTransport,
        siteIds: [],
      },
      useSiteAssignment: options => useGetTransportSiteAssignment(props.initialTransport?.id, options),
      useUpsert: options => useUpsertTransport(props.initialTransport?.id, options),
      toDto: form => ({
        ...form,
        maxWeight: form.maxWeight ? Number(form.maxWeight) : undefined,
        number: form.number ? form.number : undefined,
      }),
      onSuccess: (transportId, form) => {
        props.onDismiss();
        props.onUpsert(transportId, form.name);
      },
    });

  const compartments = useFieldArray({ control, name: 'compartments' });

  const addCompartment = () => {
    compartments.append({ capacity: 0, capacityUnit: 'Pound' });
  }

  return (<EntityModal
    mode={props.mode}
    entityName="transport"
    displayMessage={displayMessage}
    loading={loading}
    loadError={loadError}
    renderSiteAssignment={<FormSiteAssignment {...hookProps('siteIds')} control={control} sites={sites} />}
    onHide={props.onDismiss}
    isSaving={isSaving}
    onSubmit={submit}
    renderForm={(<>
      <RowFormText {...hookProps('name')}
        options={{
          required: 'Name is required',
          validate: { length: v => v.length < 51 || 'Name cannot be longer than 50 characters' }
        }} />
      <RowFormText {...hookProps('number')}
        options={{
          validate: { length: v => v.length < 51 || 'Number cannot be longer than 50 characters' }
        }} />
      <RowFormText {...hookProps('maxWeight')}
        options={{
          validate: {
            number: v => v ? !isNaN(Number(v)) || 'Must be a number' : true,
            greaterThanZero: v => v ? v > 0 || 'Must be a non-zero amount' : true,
            maxWeight: v => v ? v <= 10000000 || 'Max weight cannot be more than 10,000,000' : true,
          }
        }}
      />
      <RowUnitsSelect
        id='weightUnit'
        unitType={UnitType.Mass}
        {...hookProps('weightUnit')}
        selectedValue={getValues('weightUnit')}
        disabled={props.mode === ModalMode.View}
      />
      <div className='row pe-2'>
        <Label className="col-form-label col">Compartments:</Label>
        {
          compartments.fields.length === 0
            ? <div className="col-8 ps-4 pt-2">No compartments</div>
            : compartments.fields.map((compartment, index) => {
              return <Row key={compartment.id} className="mb-2">
                <div className="col-1" />
                <div className="col-2 ps-4">
                  <label className="col-form-label form-label" htmlFor={`compartments.${index}.capacity`}>
                    {`${index + 1}:`}
                  </label>
                </div>
                <div className="col-4">
                  <FormTextControl {...hookProps(`compartments.${index}.capacity`)}
                    options={{
                      valueAsNumber: true,
                      validate: {
                        number: v => !isNaN(Number(v)) || 'Must be a number',
                        greaterThanZero: v => v > 0 || 'Must be a non-zero amount',
                        maxWeight: v => v ? v <= 10000000 || 'Cannot be more than 10,000,000' : true,
                      }
                    }}
                  />
                </div>
                <div className="col-4">
                  <UnitsSelectControl
                    id={`compartment${index}`}
                    {...hookProps(`compartments.${index}.capacityUnit`)}
                    selectedValue={getValues(`compartments.${index}.capacityUnit`)}
                    disabled={props.mode === ModalMode.View} />
                </div>
                {props.mode !== ModalMode.View &&
                  <div className="col-1 pe-0">
                    <BoldButton aria-label='Remove compartment' className='btn-close btn-sm mt-2' onClick={() => compartments.remove(index)} />
                  </div>}
              </Row>
            })
        }
        <Row className="justify-content-end pt-2">
          <BoldButton
            className='col-auto btn-ghost-primary'
            disabled={compartments.fields.length >= 8}
            onClick={addCompartment}>
            Add compartment
          </BoldButton>
        </Row>
      </div>
    </>)} />);
};
